import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Usuario } from '../../interfaces/transaccion.interface';
import { UserService } from '../../services/user.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { RutFormat, formatRut } from '@fdograph/rut-utilities';
import { FormControl, FormGroup, Validators, FormBuilder } from '@angular/forms';
import { TransaccionService } from '../../services/transaccion.service';
import { formatDate } from 'src/app/shared/utils/date.util';
import { MatTableDataSource } from '@angular/material/table';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CartolaService } from '../../services/cartola.service';
import { ManualDepositComponent } from '../manual-deposit/manual-deposit.component';
import { MatDialog } from '@angular/material/dialog';
import { ParameterService } from '../../services/parameter.service';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { ParameterTypeEnum } from 'src/app/shared/enums/parameter-type.enum';
import { CreditLineService } from '../../services/credit-line.service';
import { LineaCredito } from '../../interfaces/credit-line.interface';
import { CreditLineDialogComponent } from '../credit-line-dialog/credit-line-dialog.component';
import Swal from 'sweetalert2';
import { BinanceWithdrawalService, ManualDebitDto, ManualCreditDto } from '../../services/binance-withdrawal.service';
import { WalletVesAdminService } from '../../services/wallet-ves-admin.service';
import { tieneSaldoVes } from 'src/app/shared/utils/saldo-ves';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-users-balance',
  templateUrl: './users-balance.component.html',
  styleUrls: ['./users-balance.component.sass'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
})
export class UsersBalanceComponent {
  userId: string = '';
  userEmail: string = '';
  userInfo: Usuario | undefined;
  saldos: any[] = [];
  operatorId: string = '';
  validUser: boolean = false;

  /**
   * `true` solo si el email del operador (por Cognito) está en la lista
   * autorizada a ABONAR (crédito manual) saldo USDT. Esa lista es un parámetro
   * propio (`CORREOS_AUT_USDT`), distinto de `CORREOS_AUT` que habilita al resto
   * de acciones. Fail-closed: ante error o sin dato, queda `false`.
   */
  puedeAbonarUsdt: boolean = false;
  columnsToDisplay: string[] = [
    'fecha',
    'descripcion',
    'saldo',
    'cargo',
    'abono',
  ];
  dataSource: MatTableDataSource<any> | undefined;
  expandedElement: any | null | undefined;
  columnsToDisplayWithExpand = [...this.columnsToDisplay, 'detalle'];
  range = new FormGroup({
    start: new FormControl<Date | null>(new Date(), [Validators.required]),
    end: new FormControl<Date | null>(new Date(), [Validators.required]),
  });
  saldoUsuario: number = 0;
  sobregiroDisponible: number | undefined = 0;
  saldoContable: number | undefined = 0;
  lineaCreditoBloqueada: boolean | undefined = false;
  lineaCredito: number | undefined = 0;
  lineaCreditoDisponible = false;
  lineaCreditoData: LineaCredito | undefined;
  saldoUSDTTotal: number = 0;
  saldoUSDTBloqueado: number = 0;
  saldoUSDTDisponible: number = 0;
  movimientosUSDT: any[] = [];
  dataSourceUSDT: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  currentPageUSDT: number = 1;
  pageSizeUSDT: number = 20;
  totalMovimientosUSDT: number = 0;
  totalPagesUSDT: number = 0;
  columnsUSDT = [
    {
      columnDef: 'fecha',
      header: 'Fecha',
      cell: (element: any) => `${element.date} ${element.time}`,
    },
    {
      columnDef: 'tipo',
      header: 'Tipo',
      cell: (element: any) => `${this.getNetworkLabel(element.network)}`,
    },
    {
      columnDef: 'saldo',
      header: 'Saldo',
      cell: (element: any) => `${this.formatUSDT(element.saldo)}`,
    },
    {
      columnDef: 'cargo',
      header: 'Cargo',
      cell: (element: any) => `${this.formatUSDT(element.debe)}`,
    },
    {
      columnDef: 'abono',
      header: 'Abono',
      cell: (element: any) => `${this.formatUSDT(element.haber)}`,
    },
    {
      columnDef: 'tasa',
      header: 'Tasa',
      cell: (element: any) => `${this.extractTasaFromDescription(element.description)}`,
    },
    {
      columnDef: 'descripcion',
      header: 'Descripción',
      cell: (element: any) => `${this.sanitizeDescription(element.description) || '-'}`,
    },
  ];
  columnsToDisplayUSDT: string[] = [
    'fecha',
    'tipo',
    'saldo',
    'cargo',
    'abono',
    'tasa',
    'descripcion',
  ];
  columns = [
    {
      columnDef: 'fecha',
      header: 'Fecha',
      cell: (element: any) =>
        `${this.datePipe.transform(element.fechaCreacion, 'dd/MM/yyyy HH:mm:ss')}`,
    },
    {
      columnDef: 'descripcion',
      header: 'Descripción',
      cell: (element: any) => `${this.getDescription(element)} `,
    },
    {
      columnDef: 'cargo',
      header: 'Cargo',
      cell: (element: any) =>
        `${this.getCargo(element.motivo, element.monedaOrigen)} `,
    },
    {
      columnDef: 'saldo',
      header: 'Saldo',
      cell: (element: any) => `${this.currencyPipe.transform(element.saldo)} `,
    },
    {
      columnDef: 'abono',
      header: 'Abono',
      cell: (element: any) =>
        `${this.getAbono(element.motivo, element.monedaOrigen)} `,
    },
  ];
  constructor(
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private transaccionService: TransaccionService,
    private route: ActivatedRoute,
    private userService: UserService,
    private spinnerService: SpinnerService,
    private cartolaService: CartolaService,
    private auth: CognitoService,
    private parameterService: ParameterService,
    private dialog: MatDialog,
    private lineaCreditoService: CreditLineService,
    private binanceWithdrawalService: BinanceWithdrawalService,
    private walletVesAdminService: WalletVesAdminService,
    private fb: FormBuilder,
  ) {
    this.userId = this.route.snapshot.params['userId'];
    this.userEmail = this.route.snapshot.params['userEmail'];

    // Inicializar formularios USDT
    this.manualDebitFormUSDT = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01), this.decimalValidator(2)]],
      reason: ['', [Validators.required, Validators.minLength(5)]]
    });

    this.manualCreditFormUSDT = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01), this.decimalValidator(2)]],
      reason: ['', [Validators.required, Validators.minLength(5)]]
    });

    this.manualCreditFormVES = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01), this.decimalValidator(2)]],
      reason: ['', [Validators.required, Validators.minLength(10)]]
    });

    this.manualDebitFormVES = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01), this.decimalValidator(2)]],
      reason: ['', [Validators.required, Validators.minLength(10)]]
    });

    this.obtenerSaldo();
    this.obtenerSaldoUSDT();
    this.getUser();
    this.initFormCuenta();
    this.search();
    this.searchUSDT();
  }

  async initFormCuenta() {
    const cognitoUser = await this.auth.getUser();
    const email: string = cognitoUser?.attributes?.['email'] ?? '';
    this.operatorId = await this.generateCod(email.toUpperCase().toString());
    const validEmails = await this.parameterService.getParameter(
      ParameterTypeEnum.CORREOS_AUTORIZADOS,
    );
    if (validEmails.valor.includes(this.operatorId)) {
      this.validUser = true;
    }

    // Abono USDT: lista autorizada aparte (`CORREOS_AUT_USDT`). A diferencia de
    // CORREOS_AUT (hashes), este parámetro guarda los correos en TEXTO PLANO, así
    // que se compara el email de Cognito directo —NO su hash— con match exacto
    // (case-insensitive) contra cada correo de la lista (separada por coma,
    // espacio o punto y coma). Fail-closed: ante error o sin dato, `false`.
    try {
      const usdtParam = await this.parameterService.getParameter(
        ParameterTypeEnum.CORREOS_AUT_USDT,
      );
      const correo = email.trim().toLowerCase();
      const autorizados = String(usdtParam?.valor ?? '')
        .toLowerCase()
        .split(/[\s,;]+/)
        .map((e) => e.trim())
        .filter((e) => !!e);
      this.puedeAbonarUsdt = !!correo && autorizados.includes(correo);
    } catch {
      this.puedeAbonarUsdt = false;
    }
  }
  async generateCod(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return hashHex;
  }

  async obtenerSaldo() {
    const {
      saldo,
      lineaCreditoId,
      balancePermitido,
      lineaBloqueada,
      sobregiro,
    } = await this.cartolaService.getSaldoCliente(this.userId);
    this.saldoUsuario = saldo;
    this.sobregiroDisponible = sobregiro;
    this.saldoContable = balancePermitido;
    this.lineaCreditoBloqueada = lineaBloqueada;
    this.lineaCredito = lineaCreditoId;
    if (
      this.lineaCredito &&
      this.lineaCredito > 0 &&
      !this.lineaCreditoBloqueada
    ) {
      this.lineaCreditoDisponible = true;
    } else {
      this.lineaCreditoDisponible = false;
    }
  }

  async obtenerSaldoUSDT() {
    try {
      this.binanceWithdrawalService
        .getUserBalanceByUserId(this.userId)
        .subscribe({
          next: (balance) => {
            this.saldoUSDTTotal = balance.totalBalance || 0;
            this.saldoUSDTBloqueado = balance.blockedBalance || 0;
            this.saldoUSDTDisponible = balance.availableBalance || 0;
          },
          error: (error) => {
            this.saldoUSDTTotal = 0;
            this.saldoUSDTBloqueado = 0;
            this.saldoUSDTDisponible = 0;
          },
        });
    } catch (error) {
      this.saldoUSDTTotal = 0;
      this.saldoUSDTBloqueado = 0;
      this.saldoUSDTDisponible = 0;
    }
  }

  downloadFile() {
    const replacer = (key: any, value: null) => (value === null ? '' : value);
    const header = ['fecha', 'saldo', 'abono', 'debito', 'descripcion', 'ref'];
    const csv = this.saldos.map((row) =>
      header.map((fieldName) => {
        if (fieldName === 'fecha') {
          return row['fechaCreacion'];
        }
        if (fieldName === 'saldo') {
          return row['saldo'];
        }
        if (fieldName === 'abono') {
          if (row.motivo === 'FONDEO WALLET') {
            return row['monedaOrigen'];
          } else {
            return 0;
          }
        }
        if (fieldName === 'debito') {
          if (row.motivo === 'FONDEO WALLET') {
            return 0;
          } else {
            return row['monedaOrigen'];
          }
        }
        if (fieldName === 'descripcion') {
          return row['banco'];
        }
        if (fieldName === 'ref') {
          return row['codigo_transferencia'];
        }
        return '';
      }),
    );
    const data = [[...header], ...csv];

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    XLSX.write(wb, { bookType: 'xlsx', type: 'buffer' });

    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'buffer' })], {
        type: 'application/octet-stream',
      }),
      'datos.xlsx',
    );
  }

  downloadFileUSDT() {
    const header = [
      'Fecha',
      'Hora',
      'Tipo',
      'Saldo',
      'Cargo',
      'Abono',
      'Tasa',
      'Descripción',
    ];
    const csv = this.movimientosUSDT.map((row) => [
      row.date || '',
      row.time || '',
      this.getNetworkLabel(row.network) || '',
      this.formatUSDT(row.saldo) || '0.00',
      this.formatUSDT(row.debe) || '0.00',
      this.formatUSDT(row.haber) || '0.00',
      this.extractTasaFromDescription(row.description),
      this.sanitizeDescription(row.description) || '',
    ]);

    const data = [[...header], ...csv];

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Movimientos USDT');

    XLSX.write(wb, { bookType: 'xlsx', type: 'buffer' });

    const fileName = `movimientos-usdt-${this.userInfo?.nombre || 'usuario'}-${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'buffer' })], {
        type: 'application/octet-stream',
      }),
      fileName,
    );
  }

  get tieneSaldoVes(): boolean {
    return tieneSaldoVes(this.userInfo?.tipoId, this.userInfo?.numeroId);
  }

  async getUser() {
    const ref = this.spinnerService.start();
    this.userService.getUserData(this.userEmail).subscribe((res: any) => {
      this.userInfo = res.user;
      // VE y chilenos (RUT) tienen saldo VES: el chileno ve CLP + VES + USDT.
      if (this.tieneSaldoVes) {
        this.obtenerSaldoVES();
        this.searchVES();
      }
      this.spinnerService.stop(ref);
    });
  }
  obtenerSaldoVES(): void {
    this.walletVesAdminService.getBalance(this.userId).subscribe({
      next: (res) => {
        if (res.success) {
          this.saldoVES = res.data.balanceVes || 0;
        }
      },
      error: () => {
        this.saldoVES = 0;
      },
    });
  }

  searchVES(): void {
    const start = this.rangeVES.value.start;
    const end = this.rangeVES.value.end;
    const startDate = start ? formatDate(start) : undefined;
    const endDate = end ? formatDate(end) : undefined;

    this.walletVesAdminService.getMovements({
      userId: this.userId,
      page: this.currentPageVES,
      limit: this.pageSizeVES,
      startDate,
      endDate,
    }).subscribe({
      next: (res) => {
        if (res.success) {
          this.dataSourceVES = new MatTableDataSource(res.data.items);
          this.totalMovimientosVES = res.data.total;
          this.totalPagesVES = res.data.totalPages;
          this.currentPageVES = res.data.page;
        }
      },
      error: (err) => {
        this.dataSourceVES = new MatTableDataSource<any>([]);
        this.errorMsgVES = typeof err === 'string' ? err : 'Error al cargar movimientos VES';
        setTimeout(() => { this.errorMsgVES = ''; }, 5000);
      },
    });
  }

  onPageChangeVES(event: PageEvent): void {
    this.currentPageVES = event.pageIndex + 1;
    this.pageSizeVES = event.pageSize;
    this.searchVES();
  }

  downloadFileVES(): void {
    const start = this.rangeVES.value.start;
    const end = this.rangeVES.value.end;
    const startDate = start ? formatDate(start) : undefined;
    const endDate = end ? formatDate(end) : undefined;

    this.walletVesAdminService.exportMovements(this.userId, startDate, endDate).subscribe({
      next: (blob) => {
        const fileName = `movimientos-ves-${this.userInfo?.nombre || 'usuario'}-${new Date().toISOString().split('T')[0]}.xlsx`;
        saveAs(blob, fileName);
      },
      error: () => {
        this.errorMsgVES = 'Error al exportar movimientos VES';
        setTimeout(() => { this.errorMsgVES = ''; }, 5000);
      },
    });
  }

  // ===== Modales VES =====

  agregarVES(): void {
    this.errorMsgVES = '';
    this.successMsgVES = '';
    this.manualCreditFormVES.reset();
    this.confirmStepCreditVES = false;
    this.confirmAmountCreditVES = '';
    this.confirmAmountMismatchVES = false;
    this.showManualCreditModalVES = true;
  }

  closeManualCreditModalVES(): void {
    this.showManualCreditModalVES = false;
    this.manualCreditFormVES.reset();
    this.errorMsgVES = '';
    this.confirmStepCreditVES = false;
    this.confirmAmountCreditVES = '';
    this.confirmAmountMismatchVES = false;
  }

  debitarVES(): void {
    this.errorMsgVES = '';
    this.successMsgVES = '';
    this.manualDebitFormVES.reset();
    this.showManualDebitModalVES = true;
  }

  closeManualDebitModalVES(): void {
    this.showManualDebitModalVES = false;
    this.manualDebitFormVES.reset();
    this.errorMsgVES = '';
  }

  onAmountInputCreditVES(event: any): void {
    let value: string = event.target.value;
    value = value.replace(/\./g, ',');
    value = value.replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 2) value = parts[0] + ',' + parts.slice(1).join('');
    if (parts.length === 2 && parts[1].length > 2) value = parts[0] + ',' + parts[1].substring(0, 2);
    event.target.value = value;
    const numericValue = value.replace(',', '.');
    this.manualCreditFormVES.patchValue({ amount: numericValue }, { emitEvent: false });
  }

  onAmountInputDebitVES(event: any): void {
    let value: string = event.target.value;
    value = value.replace(/\./g, ',');
    value = value.replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 2) value = parts[0] + ',' + parts.slice(1).join('');
    if (parts.length === 2 && parts[1].length > 2) value = parts[0] + ',' + parts[1].substring(0, 2);
    event.target.value = value;
    const numericValue = value.replace(',', '.');
    this.manualDebitFormVES.patchValue({ amount: numericValue }, { emitEvent: false });
  }

  goToConfirmStepCreditVES(): void {
    if (this.manualCreditFormVES.invalid) {
      this.manualCreditFormVES.markAllAsTouched();
      return;
    }
    this.confirmAmountCreditVES = '';
    this.confirmAmountMismatchVES = false;
    this.confirmStepCreditVES = true;
  }

  goBackFromConfirmCreditVES(): void {
    this.confirmStepCreditVES = false;
    this.confirmAmountCreditVES = '';
    this.confirmAmountMismatchVES = false;
  }

  onConfirmAmountInputCreditVES(event: any): void {
    let value: string = event.target.value;
    value = value.replace(/\./g, ',');
    value = value.replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 2) value = parts[0] + ',' + parts.slice(1).join('');
    if (parts.length === 2 && parts[1].length > 2) value = parts[0] + ',' + parts[1].substring(0, 2);
    event.target.value = value;
    this.confirmAmountCreditVES = value.replace(',', '.');
    this.confirmAmountMismatchVES = false;
  }

  get confirmAmountMatchesCreditVES(): boolean {
    const original = this.manualCreditFormVES.get('amount')?.value;
    if (!original || !this.confirmAmountCreditVES) return false;
    return parseFloat(original) === parseFloat(this.confirmAmountCreditVES);
  }

  executeManualCreditVES(): void {
    if (this.isProcessingCreditVES) return;
    if (!this.userInfo || this.manualCreditFormVES.invalid) return;
    if (!this.confirmAmountMatchesCreditVES) {
      this.confirmAmountMismatchVES = true;
      return;
    }

    const amountValue = this.manualCreditFormVES.get('amount')?.value;
    const reason = this.manualCreditFormVES.get('reason')?.value;
    const amount = typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue;

    this.errorMsgVES = '';
    this.successMsgVES = '';
    this.isProcessingCreditVES = true;

    this.walletVesAdminService
      .manualCredit({ userId: this.userId, amount, reason: reason.trim() })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.isProcessingCreditVES = false; })
      )
      .subscribe({
        next: () => {
          this.successMsgVES = 'Saldo VES agregado exitosamente';
          this.closeManualCreditModalVES();
          this.obtenerSaldoVES();
          this.searchVES();
          setTimeout(() => { this.successMsgVES = ''; }, 5000);
        },
        error: (error) => {
          this.errorMsgVES = typeof error === 'string' ? error : 'Error al agregar saldo VES';
        },
      });
  }

  executeManualDebitVES(): void {
    if (this.isProcessingDebitVES) return;
    if (!this.userInfo || this.manualDebitFormVES.invalid) return;

    const amountValue = this.manualDebitFormVES.get('amount')?.value;
    const reason = this.manualDebitFormVES.get('reason')?.value;
    const amount = typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue;

    if (amount > this.saldoVES) {
      this.errorMsgVES = 'El monto a debitar excede el saldo disponible del usuario';
      return;
    }

    this.errorMsgVES = '';
    this.successMsgVES = '';
    this.isProcessingDebitVES = true;

    this.walletVesAdminService
      .manualDebit({ userId: this.userId, amount, reason: reason.trim() })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.isProcessingDebitVES = false; })
      )
      .subscribe({
        next: () => {
          this.successMsgVES = 'Saldo VES debitado exitosamente';
          this.closeManualDebitModalVES();
          this.obtenerSaldoVES();
          this.searchVES();
          setTimeout(() => { this.successMsgVES = ''; }, 5000);
        },
        error: (error) => {
          this.errorMsgVES = typeof error === 'string' ? error : 'Error al debitar saldo VES';
        },
      });
  }

  getDescription(data: any) {
    if (data.motivo === 'FONDEO WALLET') {
      if (data.remitente?.trim()) {
        return `Fondeo de Wallet de ${data.remitente?.trim() || data.pagador || 'Aguacate Wallet'}`;
      }
      return `Fondeo de Wallet manual`;
    }
    // La REVERSIÓN va antes que el beneficiario, y no es un detalle de estilo.
    //
    // Rechazar una transferencia deja DOS filas en `balances` con el mismo
    // `order_id`: el cargo original y el abono de reversión
    // (`reject-transaction.ts:71-86`). La query las junta con la MISMA
    // transacción (`LEFT JOIN transacciones t ON t.id = b2.order_id`), así que
    // las dos traen el mismo beneficiario, la misma tasa y el mismo estado.
    //
    // Con el orden anterior, la rama del beneficiario capturaba también al
    // abono y las dos filas se rotulaban «Transferencia enviada a X - FIAT»:
    // la reversión quedaba indistinguible del envío y parecía que la operación
    // se había hecho dos veces. Los importes siempre estuvieron bien —
    // `getCargo`/`getAbono` sí distinguen la reversión—, pero el rótulo no.
    if (data.motivo?.includes('Reversión')) {
      const aQuien = data.beneficiario ?? data.beneficiarioZelle;
      return aQuien
        ? `Reversión del envío a ${aQuien} — devuelto al saldo`
        : `Transferencia de Reversión`;
    }
    if (data.beneficiario !== null) {
      return `Transferencia enviada a ${data.beneficiario} - ${data.tipoTransaccion}`;
    }
    if (data.beneficiarioZelle !== null) {
      return `Transferencia enviada a ${data.beneficiarioZelle} - ${data.tipoTransaccion}`;
    }
    if (data.motivo === 'AJUSTE DE BALANCE' && data.monedaOrigen < 0) {
      return `Debito manual`;
    }
    if (data.tipoTransaccion === 'AJUSTE' && data.motivo?.trim()) {
      return this.describeMovimientoSinTransaccion(data.motivo.trim());
    }
    if (data.tipoTransaccion === 'RETIRO') {
      return `Transaccion de ${data.tipoTransaccion}`;
    }
    if (data.tipoTransaccion !== '') {
      return `Transaccion ${data.tipoTransaccion}`;
    }
    return `Movimiento sin Transaccion`;
  }

  /**
   * Describe un movimiento de `balances` que no tiene transacción asociada
   * (`order_id` nulo, o apuntando a una transacción ya borrada).
   *
   * La query de movimientos de wallet-back rotula esos casos con
   * `COALESCE(t.tipo_trasacccion, 'AJUSTE')`, así que sin esta rama todos
   * caían en "Transaccion AJUSTE" — el caso más común es la conversión
   * CLP→USDT, que por diseño no crea fila en `transacciones` (acredita el
   * USDT en el cardex y debita CLP por SQS).
   *
   * El `motivo` que graba el backend ya es descriptivo y trae el detalle
   * (monto, tasa, destino), así que se usa tal cual. Solo se traduce el de
   * la conversión CLP→USDT, cuyo texto es técnico.
   */
  private describeMovimientoSinTransaccion(motivo: string): string {
    if (motivo.startsWith('Conversion CLP/USDT')) {
      return 'Compra de USDT con saldo CLP';
    }
    return motivo;
  }

  getCargo(motivo: string, monedaOrigen: number) {
    if (motivo === 'FONDEO WALLET' || motivo.includes('Reversión')) {
      return 0;
    }
    return this.currencyPipe.transform(Math.abs(monedaOrigen));
  }
  getAbono(motivo: string, monedaOrigen: number) {
    if (motivo === 'FONDEO WALLET' || motivo.includes('Reversión')) {
      return this.currencyPipe.transform(Math.abs(monedaOrigen));
    }
    return 0;
  }
  formatDocument() {
    if (this.userInfo?.tipoId === 'RUT') {
      return formatRut(this.userInfo.numeroId, RutFormat.DOTS_DASH);
    }
    return this.userInfo?.numeroId;
  }

  async search() {
    const ref = this.spinnerService.start();
    const saldos = await this.transaccionService.getTransaccionClienteByDate(
      this.userEmail,
      formatDate(this.range.controls['start'].value),
      formatDate(this.range.controls['end'].value),
    );
    // La cartola CLP no debe listar operaciones USDT↔VES: no afectan el
    // saldo en pesos y ya se muestran en la sección de transacciones USDT.
    // Sin este filtro aparecían duplicadas en "Saldo CLP".
    const soloClp = (saldos || []).filter(
      (t: any) => t?.tipoTransaccion !== 'USDT_VES',
    );
    this.saldos = soloClp;
    this.spinnerService.stop(ref);
    this.dataSource = new MatTableDataSource(soloClp);
  }

  async agregar() {
    const isMobile = window.innerWidth < 640;
    this.dialog
      .open(ManualDepositComponent, {
        data: {
          userInfo: this.userInfo,
          saldo: this.saldoUsuario,
          tipo: 'Agregar',
        },
        disableClose: false,
        maxWidth: isMobile ? '95vw' : '540px',
        minWidth: isMobile ? '90vw' : '480px',
        panelClass: 'aguacate-dialog',
      })
      .afterClosed()
      .subscribe((res) => {
        setTimeout(() => {
          this.obtenerSaldo();
        }, 1000);
        this.obtenerSaldo();
      });
  }

  async debitar() {
    const isMobile = window.innerWidth < 640;
    this.dialog
      .open(ManualDepositComponent, {
        data: {
          userInfo: this.userInfo,
          saldo: this.saldoContable,
          tipo: 'Debitar',
        },
        disableClose: false,
        maxWidth: isMobile ? '95vw' : '540px',
        minWidth: isMobile ? '90vw' : '480px',
        panelClass: 'aguacate-dialog',
      })
      .afterClosed()
      .subscribe((res) => {
        setTimeout(() => {
          this.obtenerSaldo();
        }, 1000);
        this.obtenerSaldo();
      });
  }

  async quitar() {
    const isMobile = window.innerWidth < 640;
    this.dialog
      .open(ManualDepositComponent, {
        data: {
          userInfo: this.userInfo,
          saldo: this.saldoUsuario,
          tipo: 'Quitar',
        },
        disableClose: false,
        maxWidth: isMobile ? '95vw' : '540px',
        minWidth: isMobile ? '90vw' : '480px',
        panelClass: 'aguacate-dialog',
      })
      .afterClosed()
      .subscribe((res) => {
        setTimeout(() => {
          this.obtenerSaldo();
        }, 1000);
        this.obtenerSaldo();
      });
  }

  async getLineaCredito() {
    await this.lineaCreditoService
      .getByUser(this.userId)
      .then((lineaCredito) => {
        if (lineaCredito) {
          this.lineaCreditoData = lineaCredito;
        }
      })
      .catch((error) => {
      });
  }

  async guardarLineaCredito() {
    if (this.lineaCreditoDisponible) {
      await this.getLineaCredito();
    }
    const isMobile = window.innerWidth < 640;
    const dialogRef = this.dialog.open(CreditLineDialogComponent, {
      maxWidth: isMobile ? '95vw' : '540px',
      minWidth: isMobile ? '90vw' : '480px',
      panelClass: 'aguacate-dialog',
      data: {
        lineaCredito: this.lineaCreditoData,
        operator: this.operatorId,
        user: this.userId,
        userEmail: this.userEmail,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      setTimeout(() => 10000);
      this.obtenerSaldo();
    });
  }

  async desactivarLineaCredito() {
    const ref = this.spinnerService.start();
    await this.lineaCreditoService.inactivateCreditLine(this.userId);
    this.lineaCreditoDisponible = false;
    this.spinnerService.stop(ref);
    Swal.fire(
      'Linea de Crédito Desactivada',
      'La linea de crédito ha sido desactivada',
      'success',
    );
    this.obtenerSaldo();
  }

  async searchUSDT() {
    const ref = this.spinnerService.start();
    const startDate = this.range.controls['start'].value
      ? formatDate(this.range.controls['start'].value)
      : undefined;
    const endDate = this.range.controls['end'].value
      ? formatDate(this.range.controls['end'].value)
      : undefined;

    this.binanceWithdrawalService
      .getUserCardexMovementsFormatted(this.userId, {
        page: this.currentPageUSDT,
        limit: this.pageSizeUSDT,
        startDate: startDate,
        endDate: endDate,
      })
      .subscribe({
        next: (response) => {
          this.movimientosUSDT = response.data;
          this.totalMovimientosUSDT = response.meta.total;
          this.totalPagesUSDT = response.meta.totalPages;
          this.dataSourceUSDT.data = response.data;
          this.spinnerService.stop(ref);
        },
        error: (error) => {
          this.movimientosUSDT = [];
          this.dataSourceUSDT = new MatTableDataSource<any>([]);
          this.spinnerService.stop(ref);
        },
      });
  }

  onPageChangeUSDT(event: PageEvent) {
    this.currentPageUSDT = event.pageIndex + 1;
    this.pageSizeUSDT = event.pageSize;
    this.searchUSDT();
  }

  formatUSDT(value: number | string): string {
    if (!value || value === 0 || value === '0') return '0.00';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '0.00';
    return numValue.toFixed(2);
  }

  sanitizeDescription(description: string | undefined): string {
    if (!description) return '-';
    return description.replace(/[\w.-]+@[\w.-]+\.\w+/g, '').replace(/:\s*$/, '').trim();
  }

  extractTasaFromDescription(description: string | undefined): string {
    if (!description) return '-';
    const m = description.match(/(?:tasa|rate)[:\s]+([0-9]*\.?[0-9]+)/i);
    if (!m) return '-';
    const n = +m[1];
    if (isNaN(n) || n <= 0) return '-';
    const natural = n < 1 ? 1 / n : n;
    return natural.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  getNetworkLabel(network: string): string {
    const networkLabels: { [key: string]: string } = {
      SWAP_USDT_CLP: 'Conversión USDT→CLP',
      SWAP_CLP_TO_USDT: 'Conversión CLP→USDT',
      SWAP_USDT_VES: 'Swap USDT→VES (beneficiario)',
      SWAP_USDT_VES_WALLET: 'Swap USDT→VES (wallet)',
      SWAP_VES_TO_USDT: 'Retiro VES→USDT',
      SEND_USDT: 'Retiro USDT',
      DEPOSIT_USDT: 'Depósito USDT',
      MANUAL_LOAD_USDT: 'Carga Manual USDT',
      ADD_USDT_MANUAL: 'Carga Manual USDT',
      DEBIT_USDT_MANUAL: 'Débito Manual USDT',
      REVERSO_SWAP_USDT_VES: 'Reverso Swap USDT→VES',
      REVERSO_RETIRO_USDT_CLP: 'Reverso Retiro USDT→CLP',
      REVERSO_RETIRO: 'Reverso Retiro',
      RETIRO_USDT_CLP: 'Retiro USDT→CLP',
      // Remesas Vita. Faltaban, y el backend devolvía DEPOSIT_USDT para todo lo
      // que no reconocía: el cargo de una remesa y el reverso de una remesa
      // rechazada se mostraban como "Depósito USDT" al lado de su propio cargo.
      PAYOUT_USDT_COP: 'Envío a Colombia (COP)',
      REVERSO_USDT_COP: 'Reverso envío COP',
      PAYOUT_USDT_PEN: 'Envío a Perú (PEN)',
      REVERSO_USDT_PEN: 'Reverso envío PEN',
    };
    return networkLabels[network] || network;
  }

  trackByUSDTId(index: number, item: any): string {
    return item.id || index.toString();
  }

  trackByColumnDef(index: number, column: any): string {
    return column.columnDef;
  }

  // Propiedades para modales USDT
  showManualDebitModalUSDT = false;
  showManualCreditModalUSDT = false;
  manualDebitFormUSDT: FormGroup;
  manualCreditFormUSDT: FormGroup;

  // Estados de carga y mensajes USDT
  loadingUSDT = false;
  errorMsgUSDT = '';
  successMsgUSDT = '';

  // Protección contra múltiples peticiones USDT
  isProcessingDebitUSDT = false;
  isProcessingCreditUSDT = false;

  // Rate limiting USDT
  debitRetryAfterSecondsUSDT = 0;
  creditRetryAfterSecondsUSDT = 0;
  debitRetryTimerUSDT: any = null;
  creditRetryTimerUSDT: any = null;

  // Confirmación de crédito USDT (paso 2)
  confirmStepCreditUSDT = false;
  confirmAmountCreditUSDT = '';
  confirmAmountMismatch = false;

  // VES
  saldoVES: number = 0;
  dataSourceVES: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  currentPageVES: number = 1;
  pageSizeVES: number = 20;
  totalMovimientosVES: number = 0;
  totalPagesVES: number = 0;
  rangeVES = new FormGroup({
    start: new FormControl<Date | null>(new Date(), [Validators.required]),
    end: new FormControl<Date | null>(new Date(), [Validators.required]),
  });
  columnsVES = [
    {
      columnDef: 'fecha',
      header: 'Fecha',
      cell: (element: any) => `${element.createdAt ? new Date(element.createdAt).toLocaleString('es-CL') : '-'}`,
    },
    {
      columnDef: 'tipo',
      header: 'Tipo',
      cell: (element: any) => this.getVesTypeLabel(element),
    },
    {
      columnDef: 'credito',
      header: 'Credito',
      cell: (element: any) => element.creditVes > 0 ? `${Number(element.creditVes).toFixed(2)}` : '-',
    },
    // 'debito' se renderiza en el template con matTooltip e ícono cuando es swap a beneficiario
    {
      columnDef: 'saldo',
      header: 'Saldo',
      cell: (element: any) => `${Number(element.balanceVes).toFixed(2)}`,
    },
    {
      columnDef: 'descripcion',
      header: 'Descripcion',
      cell: (element: any) => `${element.description || '-'}`,
    },
  ];

  isSwapBeneficiarioRow(element: any): boolean {
    return element?.type === 'RETIRO_VES'
      && typeof element?.referenceId === 'string'
      && element.referenceId.startsWith('SWAP-USDT-VES-');
  }

  getVesTypeLabel(element: any): string {
    const base = this.isSwapBeneficiarioRow(element)
      ? 'Swap a Beneficiario'
      : (element?.type || '-');
    const status = (element?.status || '').toUpperCase();
    if (status === 'PENDING') return `${base} [PENDIENTE]`;
    if (status === 'REJECTED') return `${base} [RECHAZADO]`;
    return base;
  }
  columnsToDisplayVES: string[] = ['fecha', 'tipo', 'credito', 'debito', 'saldo', 'descripcion'];

  // Modales VES
  showManualCreditModalVES = false;
  showManualDebitModalVES = false;
  manualCreditFormVES: FormGroup;
  manualDebitFormVES: FormGroup;
  isProcessingCreditVES = false;
  isProcessingDebitVES = false;
  errorMsgVES = '';
  successMsgVES = '';
  confirmStepCreditVES = false;
  confirmAmountCreditVES = '';
  confirmAmountMismatchVES = false;

  // Subject para cleanup
  private destroy$ = new Subject<void>();

  /**
   * Validador personalizado para limitar decimales
   */
  decimalValidator(maxDecimals: number) {
    return (control: any) => {
      if (!control.value) return null;

      const value = control.value.toString();
      const decimalPart = value.includes(',') ? value.split(',')[1] : value.split('.')[1];

      if (decimalPart && decimalPart.length > maxDecimals) {
        return { maxDecimals: { max: maxDecimals, actual: decimalPart.length } };
      }

      return null;
    };
  }

  /**
   * Maneja el input del campo amount para permitir solo comas como separador decimal (crédito USDT)
   */
  onAmountInputCreditUSDT(event: any): void {
    let value = event.target.value;
    value = value.replace(/\./g, ',');
    value = value.replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + ',' + parts[1].substring(0, 2);
    }
    event.target.value = value;
    const numericValue = value.replace(',', '.');
    this.manualCreditFormUSDT.patchValue({ amount: numericValue }, { emitEvent: false });
  }

  /**
   * Maneja el input del campo amount para permitir solo comas como separador decimal (débito USDT)
   */
  onAmountInputDebitUSDT(event: any): void {
    let value = event.target.value;
    value = value.replace(/\./g, ',');
    value = value.replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + ',' + parts[1].substring(0, 2);
    }
    event.target.value = value;
    const numericValue = value.replace(',', '.');
    this.manualDebitFormUSDT.patchValue({ amount: numericValue }, { emitEvent: false });
  }

  /**
   * Abre el modal de crédito manual USDT
   */
  agregarUSDT(): void {
    this.errorMsgUSDT = '';
    this.successMsgUSDT = '';
    this.manualCreditFormUSDT.reset();
    this.confirmStepCreditUSDT = false;
    this.confirmAmountCreditUSDT = '';
    this.confirmAmountMismatch = false;
    this.showManualCreditModalUSDT = true;
  }

  /**
   * Cierra el modal de crédito manual USDT
   */
  closeManualCreditModalUSDT(): void {
    this.showManualCreditModalUSDT = false;
    this.manualCreditFormUSDT.reset();
    this.errorMsgUSDT = '';
    this.confirmStepCreditUSDT = false;
    this.confirmAmountCreditUSDT = '';
    this.confirmAmountMismatch = false;
  }

  /**
   * Abre el modal de débito manual USDT
   */
  debitarUSDT(): void {
    this.errorMsgUSDT = '';
    this.successMsgUSDT = '';
    this.manualDebitFormUSDT.reset();
    this.showManualDebitModalUSDT = true;
  }

  /**
   * Cierra el modal de débito manual USDT
   */
  closeManualDebitModalUSDT(): void {
    this.showManualDebitModalUSDT = false;
    this.manualDebitFormUSDT.reset();
    this.errorMsgUSDT = '';
  }

  /**
   * Maneja errores de rate limiting (429) extrayendo información detallada
   */
  private handleRateLimitErrorUSDT(error: any, operationType: 'DEBIT' | 'CREDIT'): string {
    if (error?.error?.statusCode === 429 || error?.status === 429) {
      const details = error?.error?.details || {};
      const retryAfter = details.retryAfterSeconds || 60;
      const previousOps = details.previousOperations || 'desconocidas';
      const currentRequests = details.currentRequests || 0;
      const maxRequests = details.maxRequests || 2;

      if (operationType === 'DEBIT') {
        this.debitRetryAfterSecondsUSDT = retryAfter;
        this.startDebitRetryTimerUSDT();
      } else {
        this.creditRetryAfterSecondsUSDT = retryAfter;
        this.startCreditRetryTimerUSDT();
      }

      return `Has excedido el límite de operaciones manuales (${currentRequests}/${maxRequests}). ` +
             `Operaciones previas: ${previousOps}. ` +
             `Por favor espera ${retryAfter} segundos antes de intentar nuevamente.`;
    }

    return error?.error?.message || error?.message ||
           `Error al ${operationType === 'DEBIT' ? 'quitar' : 'agregar'} el saldo`;
  }

  /**
   * Inicia el temporizador de reintento para débito USDT
   */
  private startDebitRetryTimerUSDT(): void {
    if (this.debitRetryTimerUSDT) {
      clearInterval(this.debitRetryTimerUSDT);
    }

    this.debitRetryTimerUSDT = setInterval(() => {
      this.debitRetryAfterSecondsUSDT--;
      if (this.debitRetryAfterSecondsUSDT <= 0) {
        clearInterval(this.debitRetryTimerUSDT);
        this.debitRetryTimerUSDT = null;
      }
    }, 1000);
  }

  /**
   * Inicia el temporizador de reintento para crédito USDT
   */
  private startCreditRetryTimerUSDT(): void {
    if (this.creditRetryTimerUSDT) {
      clearInterval(this.creditRetryTimerUSDT);
    }

    this.creditRetryTimerUSDT = setInterval(() => {
      this.creditRetryAfterSecondsUSDT--;
      if (this.creditRetryAfterSecondsUSDT <= 0) {
        clearInterval(this.creditRetryTimerUSDT);
        this.creditRetryTimerUSDT = null;
      }
    }, 1000);
  }

  /**
   * Avanza al paso de confirmación del crédito USDT
   */
  goToConfirmStepCreditUSDT(): void {
    if (this.manualCreditFormUSDT.invalid) {
      this.manualCreditFormUSDT.markAllAsTouched();
      return;
    }
    this.confirmAmountCreditUSDT = '';
    this.confirmAmountMismatch = false;
    this.confirmStepCreditUSDT = true;
  }

  /**
   * Vuelve al paso 1 del modal de crédito USDT
   */
  goBackFromConfirmCreditUSDT(): void {
    this.confirmStepCreditUSDT = false;
    this.confirmAmountCreditUSDT = '';
    this.confirmAmountMismatch = false;
  }

  /**
   * Maneja el input del monto de confirmación
   */
  onConfirmAmountInputCreditUSDT(event: any): void {
    let value: string = event.target.value;
    value = value.replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + ',' + parts[1].substring(0, 2);
    }
    event.target.value = value;
    this.confirmAmountCreditUSDT = value.replace(',', '.');
    this.confirmAmountMismatch = false;
  }

  /**
   * Verifica si el monto de confirmación coincide con el original
   */
  get confirmAmountMatchesCreditUSDT(): boolean {
    const original = this.manualCreditFormUSDT.get('amount')?.value;
    if (!original || !this.confirmAmountCreditUSDT) return false;
    return parseFloat(original) === parseFloat(this.confirmAmountCreditUSDT);
  }

  /**
   * Ejecuta el crédito manual USDT
   */
  executeManualCreditUSDT(): void {
    // Candado de abono USDT: solo el operador autorizado. Cubre el caso de que
    // alguien fuerce el botón desde devtools; el `*ngIf` del template ya lo
    // oculta al resto. (El candado duro definitivo debe vivir también en backend.)
    if (!this.puedeAbonarUsdt) {
      this.errorMsgUSDT =
        'Tu usuario no está autorizado para abonar saldo USDT.';
      return;
    }

    if (this.isProcessingCreditUSDT) {
      return;
    }

    if (this.creditRetryAfterSecondsUSDT > 0) {
      this.errorMsgUSDT = `Debes esperar ${this.creditRetryAfterSecondsUSDT} segundos antes de realizar otra operación de crédito`;
      return;
    }

    if (!this.userInfo || this.manualCreditFormUSDT.invalid) {
      return;
    }

    if (!this.confirmAmountMatchesCreditUSDT) {
      this.confirmAmountMismatch = true;
      return;
    }

    const amountValue = this.manualCreditFormUSDT.get('amount')?.value;
    const reason = this.manualCreditFormUSDT.get('reason')?.value;
    const amount = typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue;

    this.errorMsgUSDT = '';
    this.successMsgUSDT = '';
    this.isProcessingCreditUSDT = true;

    const dto: ManualCreditDto = {
      userId: this.userId,
      amount: amount,
      reason: reason.trim()
    };


    this.binanceWithdrawalService
      .manualCredit(this.userId, dto)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isProcessingCreditUSDT = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.successMsgUSDT = 'Saldo USDT agregado exitosamente';
          this.closeManualCreditModalUSDT();
          this.obtenerSaldoUSDT();
          this.searchUSDT();

          setTimeout(() => {
            this.successMsgUSDT = '';
          }, 5000);
        },
        error: (error) => {
          if (typeof error === 'string') {
            this.errorMsgUSDT = error;
          } else {
            this.errorMsgUSDT = this.handleRateLimitErrorUSDT(error, 'CREDIT');
          }
        }
      });
  }

  /**
   * Ejecuta el débito manual USDT
   */
  executeManualDebitUSDT(): void {
    if (this.isProcessingDebitUSDT) {
      return;
    }

    if (this.debitRetryAfterSecondsUSDT > 0) {
      this.errorMsgUSDT = `Debes esperar ${this.debitRetryAfterSecondsUSDT} segundos antes de realizar otra operación de débito`;
      return;
    }

    if (!this.userInfo || this.manualDebitFormUSDT.invalid) {
      return;
    }

    const amountValue = this.manualDebitFormUSDT.get('amount')?.value;
    const reason = this.manualDebitFormUSDT.get('reason')?.value;
    const amount = typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue;

    if (amount > this.saldoUSDTDisponible) {
      this.errorMsgUSDT = 'El monto a debitar excede el saldo disponible del usuario';
      return;
    }

    this.errorMsgUSDT = '';
    this.successMsgUSDT = '';
    this.isProcessingDebitUSDT = true;

    const dto: ManualDebitDto = {
      userId: this.userId,
      amount: amount,
      reason: reason.trim()
    };


    this.binanceWithdrawalService
      .manualDebit(this.userId, dto)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isProcessingDebitUSDT = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.successMsgUSDT = 'Saldo USDT quitado exitosamente';
          this.closeManualDebitModalUSDT();
          this.obtenerSaldoUSDT();
          this.searchUSDT();

          setTimeout(() => {
            this.successMsgUSDT = '';
          }, 5000);
        },
        error: (error) => {
          if (typeof error === 'string') {
            this.errorMsgUSDT = error;
          } else {
            this.errorMsgUSDT = this.handleRateLimitErrorUSDT(error, 'DEBIT');
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.debitRetryTimerUSDT) {
      clearInterval(this.debitRetryTimerUSDT);
    }
    if (this.creditRetryTimerUSDT) {
      clearInterval(this.creditRetryTimerUSDT);
    }
  }
}
